import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box, Paper, Typography, Chip, Card, CardContent, Grid, Button,
  TextField, Alert, Tooltip, MenuItem, FormControlLabel, Switch,
  Stack, IconButton,
  Divider
} from '@mui/material';
import {
  Drafts as EmailTemplateIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  Code as CodeIcon,
  Visibility as PreviewIcon,
  FormatBold as BoldIcon,
  FormatItalic as ItalicIcon,
  FormatUnderlined as UnderlineIcon,
  FormatListBulleted as ListBulletedIcon,
  FormatListNumbered as ListNumberedIcon,
  TableChart as TableChartIcon,
  HorizontalRule as HrIcon,
  Label as TagIcon,
  Refresh as RefreshIcon,
  Translate as LanguageIcon
} from '@mui/icons-material';
import GenericPage, { useDefaultViewMode } from '../../Commun/components/GenericPage';
import { blobticMessage } from '../../Commun/components/BlobticMessage';
import GenericTable from '../../Commun/components/GenericTable';
import { academyStore } from '../services/academyStore';
import { EmailTemplate, STANDARD_EMAIL_TAGS } from '../types/academy';
import { useLanguage } from '../../Commun/contexts/LanguageContext';

const ENTITY_KEY = 'Blobtic_Academy,academy_email_template';
const PRIMARY_KEY = 'TPL_IDF';

const dtoToTemplate = (dto: any): EmailTemplate => ({
  id: dto.TPL_IDF || dto.id,
  uid: dto.TPL_UIDF || dto.uid,
  dos_idf: dto.DOS_IDF ? Number(dto.DOS_IDF) : undefined,
  dos_code: dto.DOS_CODE || dto.dos_code,
  dos_lib: dto.DOS_LIB || dto.dos_lib,
  code: dto.TPL_CODE || dto.code || '',
  mail_type: dto.TPL_MAIL_TYPE || dto.mail_type || 'GENERAL',
  lang: dto.TPL_LANG || dto.lang || 'fr',
  name: dto.TPL_NAME || dto.name || '',
  subject: dto.TPL_SUBJECT || dto.subject || '',
  body: dto.TPL_BODY || dto.body || '',
  tags: dto.TPL_TAGS || dto.tags || '',
  is_active: dto.TPL_IS_ACTIVE != null ? Boolean(dto.TPL_IS_ACTIVE === true || dto.TPL_IS_ACTIVE === 1 || dto.TPL_IS_ACTIVE === '1') : (dto.is_active ?? true),
  description: dto.TPL_DESCRIPTION || dto.description || '',
  user_lib: dto.USER_LIB || dto.user_lib || undefined,
  last_updt: dto.LAST_UPDT
    ? (typeof dto.LAST_UPDT === 'string' ? dto.LAST_UPDT : new Date(dto.LAST_UPDT).toLocaleString('fr-DZ'))
    : (dto.last_updt || undefined),
});

const templateToDto = (t: Partial<EmailTemplate>): Record<string, any> => ({
  TPL_IDF: (t as any).TPL_IDF ?? t.id,
  TPL_CODE: t.code || `${t.mail_type}_${(t.lang || 'FR').toUpperCase()}`,
  TPL_MAIL_TYPE: t.mail_type || 'GENERAL',
  TPL_LANG: t.lang || 'fr',
  TPL_NAME: t.name || '',
  TPL_SUBJECT: t.subject || '',
  TPL_BODY: t.body || '',
  TPL_TAGS: t.tags || '',
  TPL_IS_ACTIVE: t.is_active !== false ? 1 : 0,
  TPL_DESCRIPTION: t.description || '',
  ...(t.dos_idf ? { DOS_IDF: t.dos_idf } : {}),
});

const emptyTemplate = (): Partial<EmailTemplate> => ({
  code: '',
  mail_type: 'DEBT_REMINDER_ADULT',
  lang: 'fr',
  name: '',
  subject: '',
  body: '',
  tags: '',
  is_active: true,
  description: '',
});

const MAIL_TYPES_LIST = [
  { value: 'DEBT_REMINDER_ADULT', labelFr: 'Rappel Dette (Adulte)', labelEn: 'Debt Reminder (Adult)', labelAr: 'تذكير بالديون (للبالغين)', color: 'primary' },
  { value: 'DEBT_REMINDER_KIDS', labelFr: 'Rappel Scolarité (Parents)', labelEn: 'Tuition Reminder (Parents)', labelAr: 'تذكير بالمستحقات (للأولياء)', color: 'secondary' },
  { value: 'AUTH_OTP', labelFr: 'Double Authentification (OTP)', labelEn: '2FA Auth (OTP)', labelAr: 'التحقق بخطوتين (OTP)', color: 'info' },
  { value: 'CHANGE_PASSWORD', labelFr: 'Changement de Mot de Passe', labelEn: 'Change Password', labelAr: 'تغيير كلمة المرور', color: 'warning' },
  { value: 'RESET_PASSWORD', labelFr: 'Réinitialisation Mot de Passe', labelEn: 'Password Reset', labelAr: 'إعادة ضبط كلمة المرور', color: 'error' },
  { value: 'DISCOUNT_REQUEST_SUBMITTED', labelFr: 'Demande Remise Soumise', labelEn: 'Discount Request Submitted', labelAr: 'طلب تخفيض قيد المراجعة', color: 'info' },
  { value: 'DISCOUNT_REQUEST_APPROVED', labelFr: 'Remise Approuvée', labelEn: 'Discount Approved', labelAr: 'تم قبول التخفيض', color: 'success' },
  { value: 'DISCOUNT_REQUEST_REJECTED', labelFr: 'Remise Rejetée', labelEn: 'Discount Rejected', labelAr: 'تم رفض التخفيض', color: 'error' },
  { value: 'STUDENT_PRE_REG_ACCEPTED', labelFr: 'Pré-inscription Acceptée', labelEn: 'Pre-registration Accepted', labelAr: 'قبول التسجيل المبدئي', color: 'success' },
  { value: 'STUDENT_PRE_REG_REFUSED', labelFr: 'Pré-inscription Refusée', labelEn: 'Pre-registration Refused', labelAr: 'رفض التسجيل المبدئي', color: 'error' },
  { value: 'PAYMENT_RECEIPT', labelFr: 'Reçu de Paiement', labelEn: 'Payment Receipt', labelAr: 'وصل استلام الدفع', color: 'success' },
  { value: 'GENERAL', labelFr: 'Général / Autre', labelEn: 'General / Other', labelAr: 'عام / آخر', color: 'default' },
];

export const EmailTemplatesPage: React.FC = () => {
  const { t, language } = useLanguage();
  const [viewMode, setViewMode] = useDefaultViewMode('grid');
  const [view, setView] = useState<'list' | 'form'>('list');
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [formData, setFormData] = useState<Partial<EmailTemplate>>(emptyTemplate());
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [filterMailType, setFilterMailType] = useState<string>('ALL');
  const [filterLang, setFilterLang] = useState<string>('ALL');
  const [filterStatus, setFilterStatus] = useState<string>('ALL');

  const bodyInputRef = useRef<HTMLTextAreaElement | null>(null);

  const loadData = async (useCache = true) => {
    setLoading(true);
    try {
      const data = await academyStore.getEmailTemplates();
      setTemplates(data || []);
    } catch (err) {
      console.error('Error loading email templates:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const openNew = () => {
    const fresh = emptyTemplate();
    const defaultTags = STANDARD_EMAIL_TAGS['DEBT_REMINDER_ADULT']?.map(tItem => tItem.tag).join(',') || '';
    fresh.tags = defaultTags;
    setFormData(fresh);
    setIsEditing(false);
    setView('form');
  };

  const openEdit = (tpl: EmailTemplate) => {
    setFormData({ ...tpl });
    setIsEditing(true);
    setView('form');
  };

  const backToList = () => {
    setView('list');
    setFormData(emptyTemplate());
  };

  const handleValidate = async () => {
    if (!formData.name?.trim()) {
      blobticMessage.warning(t('common.messages.required'), t('academy.email_templates.err_name'));
      return false;
    }
    if (!formData.mail_type?.trim()) {
      blobticMessage.warning(t('common.messages.required'), t('academy.email_templates.err_type'));
      return false;
    }
    if (!formData.subject?.trim()) {
      blobticMessage.warning(t('common.messages.required'), t('academy.email_templates.err_subject'));
      return false;
    }
    if (!formData.body?.trim()) {
      blobticMessage.warning(t('common.messages.required'), t('academy.email_templates.err_body'));
      return false;
    }

    try {
      if (isEditing && formData.id) {
        const res = await academyStore.updateEmailTemplate(formData.id, formData);
        if (res.success) {
          blobticMessage.success(t('common.messages.success'), t('academy.email_templates.msg_updated'));
          await loadData(false);
          setView('list');
          return true;
        } else {
          blobticMessage.error(t('common.error'), res.message || 'Erreur lors de la mise à jour.');
          return false;
        }
      } else {
        const res = await academyStore.createEmailTemplate(formData);
        if (res.success) {
          blobticMessage.success(t('common.messages.success'), t('academy.email_templates.msg_created'));
          await loadData(false);
          setView('list');
          return true;
        } else {
          blobticMessage.error(t('common.error'), res.message || 'Erreur lors de la création.');
          return false;
        }
      }
    } catch (err: any) {
      blobticMessage.error(t('common.error'), err.message || 'Une erreur est survenue.');
      return false;
    }
  };

  const handleDelete = async (tpl: EmailTemplate) => {
    const confirmed = await blobticMessage.confirm({
      title: t('academy.email_templates.confirm_delete_title'),
      message: t('academy.email_templates.confirm_delete_msg', tpl.name),
      confirmText: t('common.actions.confirm'),
      cancelText: t('btn.cancel'),
      severity: 'warning'
    });
    if (!confirmed) return;

    try {
      const res = await academyStore.deleteEmailTemplate(tpl.id);
      if (res.success) {
        blobticMessage.success(t('common.messages.success'), t('academy.email_templates.msg_deleted'));
        await loadData(false);
      } else {
        blobticMessage.error(t('common.error'), res.message || 'Erreur lors de la suppression.');
      }
    } catch (err: any) {
      blobticMessage.error(t('common.error'), err.message || 'Une erreur est survenue.');
    }
  };

  const insertTagAtCursor = (tag: string) => {
    const textarea = bodyInputRef.current;
    if (!textarea) {
      setFormData(prev => ({ ...prev, body: (prev.body || '') + tag }));
      return;
    }

    const start = textarea.selectionStart || 0;
    const end = textarea.selectionEnd || 0;
    const currentText = formData.body || '';
    const updatedText = currentText.substring(0, start) + tag + currentText.substring(end);

    setFormData(prev => ({ ...prev, body: updatedText }));

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + tag.length, start + tag.length);
    }, 50);
  };

  const insertHtmlSnippet = (snippet: string) => {
    insertTagAtCursor(snippet);
  };

  const availableTags = useMemo(() => {
    const mailType = formData.mail_type || 'GENERAL';
    const tagMeta = STANDARD_EMAIL_TAGS[mailType] || STANDARD_EMAIL_TAGS['GENERAL'] || [];
    const tagsFromDb = formData.tags ? formData.tags.split(',').map(s => s.trim()).filter(Boolean) : [];
    if (tagsFromDb.length > 0) {
      return tagsFromDb.map(tag => {
        const meta = tagMeta.find(m => m.tag === tag);
        return { tag, labelFr: meta?.labelFr ?? tag, labelEn: meta?.labelEn ?? tag, labelAr: meta?.labelAr ?? tag };
      });
    }
    return tagMeta;
  }, [formData.mail_type, formData.tags]);

  const filteredTemplates = useMemo(() => {
    return templates.filter(t => {
      if (filterMailType !== 'ALL' && t.mail_type !== filterMailType) return false;
      if (filterLang !== 'ALL' && t.lang?.toLowerCase() !== filterLang.toLowerCase()) return false;
      if (filterStatus !== 'ALL') {
        const isActive = filterStatus === '1';
        if (t.is_active !== isActive) return false;
      }
      return true;
    });
  }, [templates, filterMailType, filterLang, filterStatus]);

  const simulatedPreviewHtml = useMemo(() => {
    let body = formData.body || '<p style="color:#999; font-style:italic;">Aucun contenu HTML...</p>';
    const sampleValues: Record<string, string> = {
      '{agencyName}': 'ACADÉMIE BLOBTIC',
      '{agencyPhone}': '+213 (0) 555 12 34 56',
      '{agencyEmail}': 'contact@BLOBTIC-academy.dz',
      '{agencyAddress}': '123 Boulevard des Martyrs, Alger',
      '{student.fullName}': 'Yacine BENALI',
      '{student.code}': 'STD-2026-0042',
      '{student.email}': 'yacine.benali@email.com',
      '{student.phone}': '+213 661 00 00 00',
      '{guardian.fullName}': 'M. Karim BENALI',
      '{guardian.relation}': 'Père',
      '{totalBalanceDue}': '18 500,00',
      '{currency}': 'DZD',
      '{otp}': '849201',
      '{expirationMinutes}': '10',
      '{userName}': 'Yacine BENALI',
      '{appName}': 'BLOBTIC ACADEMY',
      '{invoiceNumber}': 'FAC-2026-0189',
      '{discountName}': 'Remise Fratrie (15%)',
      '{discountPercent}': '15',
      '{originalAmount}': '20 000,00',
      '{newAmount}': '17 000,00',
      '{justification}': 'Inscription simultanée de 3 enfants',
      '{requestedByName}': 'Sarah Responsable Pédagogique',
      '{reviewedByName}': 'Directeur Général',
      '{reviewComment}': 'Accordé pour l\'année scolaire',
      '{courseTitle}': 'Développement Web Fullstack & IA',
      '{paymentNumber}': 'REC-2026-0087',
      '{paymentDate}': '30/08/2026',
      '{paymentAmount}': '10 000,00',
      '{paymentMethod}': 'Virement Bancaire',
      '{restToPay}': '8 500,00',
      '{invoices.table}': `<table style="width:100%; border-collapse:collapse; margin:15px 0; font-size:13px;">
        <thead>
          <tr style="background-color:#f0f4f8;">
            <th style="padding:8px; border-bottom:2px solid #cbd5e1; text-align:left;">N° Facture</th>
            <th style="padding:8px; border-bottom:2px solid #cbd5e1; text-align:left;">Formation</th>
            <th style="padding:8px; border-bottom:2px solid #cbd5e1; text-align:left;">Date</th>
            <th style="padding:8px; border-bottom:2px solid #cbd5e1; text-align:left;">Total</th>
            <th style="padding:8px; border-bottom:2px solid #cbd5e1; text-align:left;">Payé</th>
            <th style="padding:8px; border-bottom:2px solid #cbd5e1; text-align:left;">Reste à payer</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style="padding:8px; border-bottom:1px solid #e2e8f0;"><strong>FAC-2026-001</strong></td>
            <td style="padding:8px; border-bottom:1px solid #e2e8f0;">Anglais Avancé C1</td>
            <td style="padding:8px; border-bottom:1px solid #e2e8f0;">15/08/2026</td>
            <td style="padding:8px; border-bottom:1px solid #e2e8f0;">12 000,00 DZD</td>
            <td style="padding:8px; border-bottom:1px solid #e2e8f0;">4 000,00 DZD</td>
            <td style="padding:8px; border-bottom:1px solid #e2e8f0; color:#dc2626; font-weight:bold;">8 000,00 DZD</td>
          </tr>
          <tr>
            <td style="padding:8px; border-bottom:1px solid #e2e8f0;"><strong>FAC-2026-002</strong></td>
            <td style="padding:8px; border-bottom:1px solid #e2e8f0;">Mathématiques Spéciales</td>
            <td style="padding:8px; border-bottom:1px solid #e2e8f0;">20/08/2026</td>
            <td style="padding:8px; border-bottom:1px solid #e2e8f0;">10 500,00 DZD</td>
            <td style="padding:8px; border-bottom:1px solid #e2e8f0;">0,00 DZD</td>
            <td style="padding:8px; border-bottom:1px solid #e2e8f0; color:#dc2626; font-weight:bold;">10 500,00 DZD</td>
          </tr>
        </tbody>
      </table>`
    };

    Object.entries(sampleValues).forEach(([k, v]) => {
      body = body.split(k).join(v);
    });

    return body;
  }, [formData.body]);

  const columns = [
    {
      id: 'is_active',
      label: t('academy.email_templates.col_status'),
      format: (val: boolean) => (
        <Chip
          icon={val ? <ActiveIcon /> : <InactiveIcon />}
          label={val ? t('academy.email_templates.status_active') : t('academy.email_templates.status_inactive')}
          color={val ? 'success' : 'default'}
          size="small"
          variant={val ? 'filled' : 'outlined'}
        />
      ),
      minWidth: 120
    },
    {
      id: 'lang',
      label: t('academy.email_templates.col_lang'),
      format: (val: string) => {
        const flag = val === 'ar' ? t('academy.email_templates.lang_ar_flag') : val === 'en' ? t('academy.email_templates.lang_en_flag') : t('academy.email_templates.lang_fr_flag');
        return <Chip label={flag} size="small" variant="outlined" sx={{ fontWeight: 'bold' }} />;
      },
      minWidth: 100
    },
    {
      id: 'mail_type',
      label: t('academy.email_templates.col_type'),
      format: (val: string) => {
        const meta = MAIL_TYPES_LIST.find(m => m.value === val);
        return (
          <Chip
            label={meta ? (language === 'ar' ? meta.labelAr : language === 'en' ? meta.labelEn : meta.labelFr) : val}
            color={(meta?.color as any) || 'primary'}
            size="small"
            variant="outlined"
          />
        );
      },
      minWidth: 200
    },
    {
      id: 'name',
      label: t('academy.email_templates.col_name'),
      format: (val: string, row: EmailTemplate) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{val}</Typography>
          <Typography variant="caption" color="text.secondary">{row.code}</Typography>
        </Box>
      )
    },
    {
      id: 'subject',
      label: t('academy.email_templates.col_subject'),
      format: (val: string) => (
        <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>{val}</Typography>
      )
    },
    {
      id: 'last_updt',
      label: t('academy.email_templates.col_updated'),
      format: (val: string, row: EmailTemplate) => (
        <Typography variant="caption" color="text.secondary">
          {val} {row.user_lib ? `(${row.user_lib})` : ''}
        </Typography>
      ),
      minWidth: 160
    }
  ];

  // ───────────────────────────────────────────────────────────────────────────
  // LIST VIEW
  // ───────────────────────────────────────────────────────────────────────────
  if (view === 'list') {
    return (
      <GenericPage
        title={t('academy.email_templates.title')}
        icon={<EmailTemplateIcon />}
        footerEnabled={false}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        additionalActions={
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={() => loadData(false)}
            >
              {t('academy.email_templates.btn_refresh')}
            </Button>

            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={openNew}
            >
              {t('academy.email_templates.btn_new')}
            </Button>
          </Stack>
        }
      >
        <Box sx={{ p: 2 }}>
          {/* Filters Bar */}
          <Paper elevation={0} sx={{ p: 2, mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4} md={4}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label={t('academy.email_templates.filter_type')}
                  value={filterMailType}
                  onChange={e => setFilterMailType(e.target.value)}
                >
                  <MenuItem value="ALL">{t('academy.email_templates.filter_type_all')}</MenuItem>
                  {MAIL_TYPES_LIST.map(m => (
                    <MenuItem key={m.value} value={m.value}>
                      {language === 'ar' ? m.labelAr : language === 'en' ? m.labelEn : m.labelFr}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={6} sm={4} md={3}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label={t('academy.email_templates.filter_lang')}
                  value={filterLang}
                  onChange={e => setFilterLang(e.target.value)}
                >
                  <MenuItem value="ALL">{t('academy.email_templates.filter_lang_all')}</MenuItem>
                  <MenuItem value="fr">{t('academy.email_templates.lang_fr_flag')} Français (FR)</MenuItem>
                  <MenuItem value="ar">{t('academy.email_templates.lang_ar_flag')} Arabe (AR)</MenuItem>
                  <MenuItem value="en">{t('academy.email_templates.lang_en_flag')} Anglais (EN)</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={6} sm={4} md={3}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label={t('academy.email_templates.filter_status')}
                  value={filterStatus}
                  onChange={e => setFilterStatus(e.target.value)}
                >
                  <MenuItem value="ALL">{t('academy.email_templates.filter_status_all')}</MenuItem>
                  <MenuItem value="1">{t('academy.email_templates.filter_active')}</MenuItem>
                  <MenuItem value="0">{t('academy.email_templates.filter_inactive')}</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} sm={12} md={2} sx={{ textAlign: 'right' }}>
                <Typography variant="caption" color="text.secondary">
                  <strong>{filteredTemplates.length}</strong> {t('academy.email_templates.results', filteredTemplates.length).replace(String(filteredTemplates.length), '').trim() || `${filteredTemplates.length} modèle(s)`}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Grid View */}
          {viewMode === 'grid' && (
            <Grid container spacing={2.5}>
              {filteredTemplates.map(tpl => {
                const meta = MAIL_TYPES_LIST.find(m => m.value === tpl.mail_type);
                const flag = tpl.lang === 'ar' ? '🇸🇦 AR' : tpl.lang === 'en' ? '🇬🇧 EN' : '🇫🇷 FR';

                return (
                  <Grid item xs={12} sm={6} md={4} key={tpl.id}>
                    <Card
                      elevation={0}
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        border: '1px solid',
                        borderColor: tpl.is_active ? 'divider' : 'error.light',
                        borderRadius: 2.5,
                        transition: 'all 0.2s',
                        opacity: tpl.is_active ? 1 : 0.75,
                        '&:hover': {
                          transform: 'translateY(-3px)',
                          boxShadow: '0 8px 24px rgba(0,0,0,0.08)'
                        }
                      }}
                    >
                      <CardContent sx={{ flex: 1, p: 2.5 }}>
                        {/* Badges Header */}
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Chip
                              label={meta ? (language === 'ar' ? meta.labelAr : language === 'en' ? meta.labelEn : meta.labelFr) : tpl.mail_type}
                              color={(meta?.color as any) || 'primary'}
                              size="small"
                              variant="filled"
                              sx={{ fontWeight: 600 }}
                            />
                            <Chip label={flag} size="small" variant="outlined" sx={{ fontWeight: 'bold' }} />
                          </Stack>

                          <Chip
                            icon={tpl.is_active ? <ActiveIcon fontSize="small" /> : <InactiveIcon fontSize="small" />}
                            label={tpl.is_active ? t('academy.email_templates.status_active') : t('academy.email_templates.status_inactive')}
                            color={tpl.is_active ? 'success' : 'default'}
                            size="small"
                            variant={tpl.is_active ? 'outlined' : 'filled'}
                          />
                        </Stack>

                        {/* Title & Code */}
                        <Typography variant="h6" sx={{ fontSize: '1.05rem', fontWeight: 'bold', mb: 0.5 }}>
                          {tpl.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                          {t('academy.email_templates.card_code', tpl.code)}
                        </Typography>

                        {/* Subject Preview */}
                        <Paper
                          elevation={0}
                          sx={{
                            p: 1.2,
                            mb: 1.5,
                            bgcolor: 'background.default',
                            border: '1px dashed',
                            borderColor: 'divider',
                            borderRadius: 1.5
                          }}
                        >
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>
                            {t('academy.email_templates.card_subject')}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem' }} noWrap>
                            {tpl.subject}
                          </Typography>
                        </Paper>

                        {/* Description */}
                        {tpl.description && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, fontStyle: 'italic' }}>
                            {tpl.description}
                          </Typography>
                        )}

                        {/* Tags Preview */}
                        {tpl.tags && (
                          <Box sx={{ mb: 1 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>
                              {t('academy.email_templates.card_vars', tpl.tags.split(',').length)}
                            </Typography>
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                              {tpl.tags.split(',').slice(0, 4).map((tag, idx) => (
                                <Chip key={idx} label={tag.trim()} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} />
                              ))}
                              {tpl.tags.split(',').length > 4 && (
                                <Chip label={`+${tpl.tags.split(',').length - 4}`} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} />
                              )}
                            </Stack>
                          </Box>
                        )}
                      </CardContent>

                      <Divider />

                      {/* Card Actions */}
                      <Box sx={{ p: 1.5, px: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'background.default' }}>
                        <Typography variant="caption" color="text.secondary">
                          {tpl.last_updt ? t('academy.email_templates.card_updated', tpl.last_updt.split(' ')[0]) : ''}
                        </Typography>

                        <Stack direction="row" spacing={1}>
                          <Tooltip title={t('academy.email_templates.card_edit')}>
                            <IconButton size="small" color="primary" onClick={() => openEdit(tpl)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title={t('academy.email_templates.card_delete')}>
                            <IconButton size="small" color="error" onClick={() => handleDelete(tpl)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Box>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}

          {/* Table View */}
          {viewMode === 'table' && (
            <GenericTable
              data={filteredTemplates}
              columns={columns}
              onEdit={openEdit}
              onDelete={handleDelete}
              searchPlaceholder={t('academy.email_templates.search_placeholder')}
              maxHeight="calc(100vh - 280px)"
            />
          )}
        </Box>
      </GenericPage>
    );
  }

  // ───────────────────────────────────────────────────────────────────────────
  // FORM / DETAIL VIEW
  // ───────────────────────────────────────────────────────────────────────────
  return (
    <GenericPage
      title={isEditing ? t('academy.email_templates.edit_title', formData.name || 'Modèle') : t('academy.email_templates.new_title')}
      icon={<EmailTemplateIcon />}
      footerEnabled={false}
      additionalActions={
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={backToList}
          >
            {t('academy.email_templates.btn_back')}
          </Button>
          <Button
            variant="outlined"
            onClick={() => setFormData(emptyTemplate())}
          >
            {t('academy.email_templates.btn_reset')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleValidate}
          >
            {isEditing ? t('academy.email_templates.btn_save') : t('academy.email_templates.btn_create')}
          </Button>
        </Stack>
      }
    >
      <Box sx={{ p: 2 }}>
        <Grid container spacing={3}>
          {/* Left Column: Form Configuration */}
          <Grid item xs={12} md={7}>

            <Paper elevation={0} sx={{ p: 2.5, mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <LanguageIcon color="primary" /> {t('academy.email_templates.section1')}
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={8}>
                  <TextField
                    required
                    fullWidth
                    size="small"
                    label={t('academy.email_templates.field_name')}
                    placeholder={t('academy.email_templates.field_name_placeholder')}
                    value={formData.name || ''}
                    onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t('academy.email_templates.field_code')}
                    placeholder={t('academy.email_templates.field_code_helper')}
                    value={formData.code || ''}
                    onChange={e => setFormData(p => ({ ...p, code: e.target.value }))}
                    helperText={t('academy.email_templates.field_code_helper')}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    required
                    fullWidth
                    size="small"
                    label={t('academy.email_templates.field_type')}
                    value={formData.mail_type || 'GENERAL'}
                    onChange={e => {
                      const newType = e.target.value;
                      const tagsForType = STANDARD_EMAIL_TAGS[newType]?.map(tItem => tItem.tag).join(',') || '';
                      setFormData(p => ({
                        ...p,
                        mail_type: newType,
                        tags: tagsForType,
                        code: `${newType}_${(p.lang || 'FR').toUpperCase()}`
                      }));
                    }}
                  >
                    {MAIL_TYPES_LIST.map(m => (
                      <MenuItem key={m.value} value={m.value}>
                        {language === 'ar' ? m.labelAr : language === 'en' ? m.labelEn : m.labelFr} ({m.value})
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    required
                    fullWidth
                    size="small"
                    label={t('academy.email_templates.field_lang')}
                    value={formData.lang || 'fr'}
                    onChange={e => {
                      const newLang = e.target.value;
                      setFormData(p => ({
                        ...p,
                        lang: newLang,
                        code: `${p.mail_type || 'GENERAL'}_${newLang.toUpperCase()}`
                      }));
                    }}
                  >
                    <MenuItem value="fr">{t('academy.email_templates.lang_fr')}</MenuItem>
                    <MenuItem value="ar">{t('academy.email_templates.lang_ar')}</MenuItem>
                    <MenuItem value="en">{t('academy.email_templates.lang_en')}</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_active !== false}
                        onChange={e => setFormData(p => ({ ...p, is_active: e.target.checked }))}
                        color="success"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {formData.is_active !== false ? t('academy.email_templates.active_label') : t('academy.email_templates.inactive_label')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formData.is_active !== false
                            ? t('academy.email_templates.active_hint')
                            : t('academy.email_templates.inactive_hint')}
                        </Typography>
                      </Box>
                    }
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    size="small"
                    label={t('academy.email_templates.field_desc')}
                    placeholder={t('academy.email_templates.field_desc_placeholder')}
                    value={formData.description || ''}
                    onChange={e => setFormData(p => ({ ...p, description: e.target.value }))}
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Subject Input Card */}
            <Paper elevation={0} sx={{ p: 2.5, mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <EmailTemplateIcon color="primary" /> {t('academy.email_templates.section2')}
              </Typography>

              <TextField
                required
                fullWidth
                size="small"
                label={t('academy.email_templates.field_subject')}
                placeholder={t('academy.email_templates.field_subject_placeholder')}
                value={formData.subject || ''}
                onChange={e => setFormData(p => ({ ...p, subject: e.target.value }))}
                dir={formData.lang === 'ar' ? 'rtl' : 'ltr'}
                helperText={t('academy.email_templates.field_subject_helper')}
              />
            </Paper>

            {/* HTML Body Card with Tags Bar */}
            <Paper elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CodeIcon color="primary" /> {t('academy.email_templates.section3')}
              </Typography>

              {/* Tag Insertion Palette */}
              <Box sx={{ p: 1.5, mb: 2, bgcolor: 'background.default', border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                  <TagIcon fontSize="small" color="secondary" /> {t('academy.email_templates.tags_hint')}
                </Typography>

                <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
                  {availableTags.map((tItem, idx) => (
                    <Tooltip
                      key={idx}
                      title={language === 'ar' ? tItem.labelAr : language === 'en' ? tItem.labelEn : tItem.labelFr}
                      arrow
                    >
                      <Chip
                        label={tItem.tag}
                        size="small"
                        color="secondary"
                        variant="outlined"
                        onClick={() => insertTagAtCursor(tItem.tag)}
                        sx={{
                          cursor: 'pointer',
                          fontWeight: 'bold',
                          '&:hover': { bgcolor: 'secondary.light', color: '#fff' }
                        }}
                      />
                    </Tooltip>
                  ))}
                </Stack>
              </Box>

              {/* HTML Snippet Toolbar */}
              <Stack direction="row" spacing={0.5} sx={{ mb: 1, p: 0.5, bgcolor: 'background.default', borderRadius: 1 }}>
                <Tooltip title="Insérer Gras <strong>">
                  <IconButton size="small" onClick={() => insertHtmlSnippet('<strong>Texte</strong>')}>
                    <BoldIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Insérer Italique <em>">
                  <IconButton size="small" onClick={() => insertHtmlSnippet('<em>Texte</em>')}>
                    <ItalicIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Insérer Souligné <u>">
                  <IconButton size="small" onClick={() => insertHtmlSnippet('<u>Texte</u>')}>
                    <UnderlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Insérer Titre <h2>">
                  <IconButton size="small" onClick={() => insertHtmlSnippet('<h2>Titre</h2>')}>
                    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>H2</Typography>
                  </IconButton>
                </Tooltip>

                <Tooltip title="Insérer Paragraphe <p>">
                  <IconButton size="small" onClick={() => insertHtmlSnippet('<p>Paragraphe...</p>')}>
                    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>P</Typography>
                  </IconButton>
                </Tooltip>

                <Tooltip title="Insérer Ligne Horizontale <hr/>">
                  <IconButton size="small" onClick={() => insertHtmlSnippet('<hr style="border:0; border-top:1px solid #e2e8f0; margin:20px 0;"/>')}>
                    <HrIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Insérer Tableau Factures">
                  <IconButton size="small" onClick={() => insertTagAtCursor('{invoices.table}')}>
                    <TableChartIcon fontSize="small" color="primary" />
                  </IconButton>
                </Tooltip>
              </Stack>

              {/* HTML Editor Textarea */}
              <TextField
                inputRef={bodyInputRef}
                required
                fullWidth
                multiline
                rows={16}
                size="small"
                label={t('academy.email_templates.field_body')}
                placeholder={t('academy.email_templates.field_body_placeholder')}
                value={formData.body || ''}
                onChange={e => setFormData(p => ({ ...p, body: e.target.value }))}
                dir={formData.lang === 'ar' ? 'rtl' : 'ltr'}
                sx={{
                  '& .MuiInputBase-input': {
                    fontFamily: 'Consolas, "Courier New", monospace',
                    fontSize: '0.85rem',
                    lineHeight: 1.4
                  }
                }}
              />
            </Paper>
          </Grid>

          {/* Right Column: Live Interactive Preview */}
          <Grid item xs={12} md={5}>
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                position: 'sticky',
                top: 20
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PreviewIcon color="primary" /> {t('academy.email_templates.preview_title')}
                </Typography>

                <Chip
                  label={formData.lang === 'ar' ? t('academy.email_templates.lang_ar_rtl') : formData.lang === 'en' ? t('academy.email_templates.lang_en_chip') : t('academy.email_templates.lang_fr_chip')}
                  size="small"
                  variant="outlined"
                  sx={{ fontWeight: 'bold' }}
                />
              </Stack>

              <Alert severity="info" sx={{ mb: 2, py: 0.5 }}>
                {t('academy.email_templates.preview_hint')}
              </Alert>

              {/* Simulated Subject Header */}
              <Box sx={{ p: 1.5, mb: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>
                  {t('academy.email_templates.preview_subject')}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#1e293b' }}>
                  {(formData.subject || '')
                    .replace('{agencyName}', 'ACADÉMIE BLOBTIC')
                    .replace('{student.fullName}', 'Yacine BENALI')
                    .replace('{totalBalanceDue}', '18 500,00')
                    .replace('{currency}', 'DZD')
                    .replace('{otp}', '849201')
                    || t('academy.email_templates.preview_empty')}
                </Typography>
              </Box>

              {/* Rendered HTML Container */}
              <Box
                sx={{
                  border: '1px solid #e2e8f0',
                  borderRadius: 2,
                  p: 2,
                  bgcolor: '#ffffff',
                  maxHeight: 'calc(100vh - 380px)',
                  overflowY: 'auto',
                  boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.02)'
                }}
                dir={formData.lang === 'ar' ? 'rtl' : 'ltr'}
                dangerouslySetInnerHTML={{ __html: simulatedPreviewHtml }}
              />
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </GenericPage>
  );
};

export default EmailTemplatesPage;
